// hemo-sim-v0 reader: a 1D network's graph and its `series/<quantity>.f32` arrays ([frame][sample]),
// plus what tissue-fate runs add (`growth.csv`). Segments map to asset-pack structures through
// `segment.structure`; several segments can share one structure (an ICA and its terminus).

import type { SimFiles } from './files.ts';
import { axisFromHeader, locate, type Frac, type TimeAxis } from './time.ts';
import type { HemoGraphRaw, HemoHeader, HemoSegmentRaw } from './types.ts';

export interface HemoSegment {
  id: string;
  index: number;
  name: string;
  structure: string | null;
  sn: number | string;
  tn: number | string;
  lengthMm: number;
  /** Columns of every series: column `offset + k` is the value at arc fraction `s[k]`. */
  samples: { offset: number; count: number; s: number[] };
  state: 'open' | 'occluded' | 'absent';
  centreline: { offset: number; count: number } | null;
  territory: string | null;
}

export interface GrowthTable {
  columns: string[];
  time: Float64Array;
  values: Record<string, Float64Array>;
}

/** A small CSV with a header row and numeric cells (`growth.csv`). */
export function parseCsv(text: string): GrowthTable {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const head = lines.shift()?.split(',').map((s) => s.trim()) ?? [];
  if (!head.length) throw new Error('empty CSV');
  const cols = head.map(() => [] as number[]);
  for (const line of lines) line.split(',').forEach((cell, i) => cols[i]?.push(Number(cell)));
  const values: Record<string, Float64Array> = {};
  head.forEach((h, i) => (values[h] = Float64Array.from(cols[i]!)));
  const timeCol = head.find((h) => /^time(_s)?$/.test(h)) ?? head[0]!;
  return { columns: head.filter((h) => h !== timeCol), time: values[timeCol]!, values };
}

function normalize(raw: HemoSegmentRaw, i: number): HemoSegment {
  const samples = raw.samples ?? { offset: i, count: 1, s: [0.5] };
  if (samples.s.length !== samples.count) throw new Error(`segment ${raw.id}: samples.s has ${samples.s.length} entries for count ${samples.count}`);
  return {
    id: raw.id,
    index: raw.index ?? i,
    name: raw.name ?? raw.id,
    structure: raw.structure ?? null,
    sn: raw.sn,
    tn: raw.tn,
    lengthMm: raw.length_mm ?? 1,
    samples,
    state: raw.state ?? 'open',
    centreline: raw.centreline ?? null,
    territory: raw.outlet?.territory ?? null,
  };
}

export class HemoSim {
  readonly axis: TimeAxis;
  readonly segments: HemoSegment[];
  /** Columns per frame in every series array. */
  readonly columns: number;
  private byStructure = new Map<string, HemoSegment[]>();

  private constructor(
    readonly files: SimFiles,
    readonly header: HemoHeader,
    graph: HemoGraphRaw | null,
  ) {
    this.axis = axisFromHeader(header.time);
    this.segments = (graph?.segments ?? []).map(normalize);
    this.columns = this.segments.reduce((m, s) => Math.max(m, s.samples.offset + s.samples.count), 0);
    for (const s of this.segments) {
      if (!s.structure) continue;
      const list = this.byStructure.get(s.structure) ?? [];
      list.push(s);
      this.byStructure.set(s.structure, list);
    }
  }

  static async open(files: SimFiles, header: HemoHeader): Promise<HemoSim> {
    const graphPath = header.files?.graph ?? (header.kind === '1d-network' ? 'graph.json' : null);
    const graph = graphPath ? await files.json<HemoGraphRaw>(graphPath) : null;
    return new HemoSim(files, header, graph);
  }

  get isNetwork(): boolean {
    return this.segments.length > 0;
  }

  /** Quantities with a series file: 'flow', 'pressure', 'velocity', 'area'. */
  get quantities(): string[] {
    return (this.header.files?.series ?? []).map((p) => /([^/]+)\.f32$/.exec(p)?.[1]).filter((q): q is string => !!q);
  }

  /** Units of a quantity from the header (`flow` → 'mL/s'). */
  unit(q: string): string {
    return this.header.units?.[q] ?? '';
  }

  /** A quantity's `[frame][column]` array, checked against frames × columns. */
  async series(q: string): Promise<Float32Array> {
    const path = (this.header.files?.series ?? []).find((p) => p.endsWith(`/${q}.f32`) || p === `${q}.f32`);
    if (!path) throw new Error(`${this.header.id} has no ${q} series`);
    const data = (await this.files.raw(path, { count: this.axis.frames * this.columns })) as Float32Array;
    return data;
  }

  /** The structure's segments, longest first (the ICA before its short terminus). */
  segmentsFor(structure: string): HemoSegment[] {
    return [...(this.byStructure.get(structure) ?? [])].sort((a, b) => b.lengthMm - a.lengthMm);
  }

  segment(id: string): HemoSegment | undefined {
    return this.segments.find((s) => s.id === id);
  }

  get structures(): string[] {
    return [...this.byStructure.keys()];
  }

  /** Columns and weights for arc fraction s on a segment (linear between its samples). */
  private columnAt(seg: HemoSegment, s: number): { c0: number; c1: number; w: number } {
    const { offset, s: at } = seg.samples;
    if (at.length === 1 || s <= at[0]!) return { c0: offset, c1: offset, w: 0 };
    for (let k = 1; k < at.length; k++)
      if (s <= at[k]!) return { c0: offset + k - 1, c1: offset + k, w: (s - at[k - 1]!) / (at[k]! - at[k - 1]!) };
    return { c0: offset + at.length - 1, c1: offset + at.length - 1, w: 0 };
  }

  /** One quantity at arc fraction s along a segment and time t: linear in s, periodic or clamped in t. */
  sample(data: Float32Array, seg: HemoSegment, s: number, t: number, frac: Frac = { i0: 0, i1: 0, w: 0 }): number {
    const { c0, c1, w } = this.columnAt(seg, s);
    const f = locate(this.axis, t, frac);
    const C = this.columns;
    const at = (i: number) => data[i * C + c0]! + (data[i * C + c1]! - data[i * C + c0]!) * w;
    const a = at(f.i0);
    return f.w ? a + (at(f.i1) - a) * f.w : a;
  }

  /** A segment's value over all frames, at arc fraction s or averaged along its samples. */
  segmentSeries(data: Float32Array, seg: HemoSegment, at: number | 'mean' = 'mean'): Float32Array {
    const out = new Float32Array(this.axis.frames);
    const C = this.columns;
    if (at === 'mean') {
      const { offset, count } = seg.samples;
      for (let f = 0; f < out.length; f++) {
        let sum = 0;
        for (let k = 0; k < count; k++) sum += data[f * C + offset + k]!;
        out[f] = sum / count;
      }
    } else {
      const { c0, c1, w } = this.columnAt(seg, at);
      for (let f = 0; f < out.length; f++) out[f] = data[f * C + c0]! + (data[f * C + c1]! - data[f * C + c0]!) * w;
    }
    return out;
  }

  /**
   * Per-structure values for overlays: `[frame][key]`, each structure the length-weighted mean of
   * its segments' arc means. `abs` takes magnitudes first (speed from signed velocity).
   */
  structureSeries(data: Float32Array, opts: { abs?: boolean } = {}): { keys: string[]; values: Float32Array } {
    const keys = this.structures;
    const F = this.axis.frames;
    const values = new Float32Array(F * keys.length);
    const src = opts.abs ? Float32Array.from(data, Math.abs) : data;
    keys.forEach((key, k) => {
      const segs = this.byStructure.get(key)!;
      const total = segs.reduce((a, s) => a + s.lengthMm, 0) || 1;
      for (const seg of segs) {
        const series = this.segmentSeries(src, seg);
        for (let f = 0; f < F; f++) values[f * keys.length + k]! += (series[f]! * seg.lengthMm) / total;
      }
    });
    return { keys, values };
  }

  /** Centreline polylines: xyz points, per-segment point offsets (by segment index), radii. */
  async centrelines(): Promise<{ points: Float32Array; offsets: Uint32Array; radius: Float32Array | null } | null> {
    const files = this.header.files;
    const path = files?.centrelines ?? (this.isNetwork ? 'centrelines.f32' : null);
    if (!path) return null;
    // Companion files are named in the header when the run says so; otherwise they sit beside the points.
    const stem = path.replace(/\.f32$/, '');
    const offsetsPath = typeof files?.centreline_offsets === 'string' ? files.centreline_offsets : `${stem}.offsets.u32`;
    const radiusPath = typeof files?.centreline_radius === 'string' ? files.centreline_radius : `${stem}.radius.f32`;
    const [points, offsets, radius] = await Promise.all([
      this.files.raw(path) as Promise<Float32Array>,
      this.files.raw(offsetsPath) as Promise<Uint32Array>,
      (this.files.raw(radiusPath) as Promise<Float32Array>).catch(() => null),
    ]);
    if (points.length % 3) throw new Error(`${path}: not xyz triples`);
    return { points, offsets, radius };
  }

  /** The segment whose centreline passes nearest a point (a click's surface mm), with the arc fraction there. */
  async nearest(mm: [number, number, number], among: HemoSegment[] = this.segments): Promise<{ segment: HemoSegment; s: number; distanceMm: number } | null> {
    const cl = await this.centrelines();
    if (!cl) return null;
    let best: { segment: HemoSegment; s: number; distanceMm: number } | null = null;
    for (const seg of among) {
      const start = seg.centreline?.offset ?? cl.offsets[seg.index];
      const count = seg.centreline?.count ?? (cl.offsets[seg.index + 1] ?? 0) - (start ?? 0);
      if (start === undefined || count < 1) continue;
      let arc = 0;
      let bestArc = 0;
      let bestD = Infinity;
      for (let k = 0; k < count; k++) {
        const p = (start + k) * 3;
        if (k > 0) arc += Math.hypot(cl.points[p]! - cl.points[p - 3]!, cl.points[p + 1]! - cl.points[p - 2]!, cl.points[p + 2]! - cl.points[p - 1]!);
        const d = Math.hypot(cl.points[p]! - mm[0], cl.points[p + 1]! - mm[1], cl.points[p + 2]! - mm[2]);
        if (d < bestD) {
          bestD = d;
          bestArc = arc;
        }
      }
      if (!best || bestD < best.distanceMm) best = { segment: seg, s: arc > 0 ? bestArc / arc : 0.5, distanceMm: bestD };
    }
    return best;
  }

  /** Tissue volumes over time (`files.growth`, tissue-fate runs), or null. */
  async growth(): Promise<GrowthTable | null> {
    const path = this.header.files?.growth;
    return path ? parseCsv(await this.files.text(path)) : null;
  }
}
